import { FormControl } from '@mui/material'
import TextField from '@mui/material/TextField'
import IntakeCard from 'candidate/components/IntakeCard'
import { QuestionWrapper } from 'candidate/components/QuestionWrapper'
import React, { useEffect, useMemo, useState } from 'react'
import NextButton from 'shared/components/NextButton'
import SecondaryButton from 'shared/components/SecondaryButton'
import TertiaryButton from 'shared/components/TertiaryButton'
import { AdditionalButtonsContainer } from '../styles'

export interface AnalystAnswer {
    id: number
    analyst: string
    error?: string
}

interface Props {
    answer?: AnalystAnswer[]
    callback: React.Dispatch<React.SetStateAction<Record<string, any>>>
    prompt: string
    subtext?: string
    skipCallback?: React.Dispatch<React.SetStateAction<string>>
    skipText?: string
}

const AnalystsQuestion: React.FC<Props> = ({
    answer,
    callback,
    prompt,
    subtext,
    skipCallback,
    skipText,
}: Props) => {
    const blankEntry: AnalystAnswer = useMemo(
        () => ({
            id: 1,
            analyst: '',
            error: '',
        }),
        []
    )

    const [disabled, setDisabled] = useState(true)
    const [entries, setEntries] = useState<AnalystAnswer[]>([blankEntry])

    // Initialize entries with the answer if it exists
    useEffect(() => {
        if (answer && answer.length > 0) {
            const allAnswers: AnalystAnswer[] = []
            answer.map((entry) => {
                const ans: AnalystAnswer = {
                    id: entry.id,
                    analyst: entry.analyst,
                    error: '',
                }
                allAnswers.push(ans)
                return ans
            })
            setEntries(allAnswers)
            setDisabled(false)
        } else {
            setEntries([blankEntry])
        }
    }, [answer, blankEntry])

    useEffect(() => {
        setDisabled(entries.some((entry) => entry.analyst === ''))
    }, [entries])

    // Add a new entry
    const addEntry = () => {
        setEntries([
            ...entries,
            {
                id: entries.length + 1,
                error: '',
                analyst: '',
            },
        ])
    }

    // Remove the last entry
    const removeEntry = () => {
        if (entries.length > 1) {
            setEntries(entries.slice(0, -1))
        }
    }

    const updateEntry = (id: number, updatedEntry: Partial<AnalystAnswer>) => {
        setEntries(
            Array.isArray(entries)
                ? entries.map((entry) => {
                      if (entry.id === id) {
                          const updated = { ...entry, ...updatedEntry }
                          if (updated.analyst.length === 0) {
                              updated.error = 'Please enter a valid name.'
                              setDisabled(true)
                          } else {
                              updated.error = ''
                              setDisabled(false)
                          }
                          return updated
                      }
                      setDisabled(false)
                      return entry
                  })
                : [blankEntry]
        )
    }

    const submitData = () => {
        const response = entries
        callback({ response })
    }

    return (
        <>
            <IntakeCard
                question={prompt}
                subtext={subtext}
                skipCallback={skipCallback}
                skipText={skipText}
            >
                <QuestionWrapper>
                    {Array.isArray(entries) &&
                        entries.map((entry, index) => (
                            <FormControl fullWidth key={entry.id}>
                                <TextField
                                    id={`analyst-input`}
                                    label="Full Name"
                                    value={entry.analyst}
                                    onChange={(e) => {
                                        updateEntry(entry.id, {
                                            analyst: e.target.value,
                                        })
                                    }}
                                    variant="outlined"
                                />
                            </FormControl>
                        ))}
                </QuestionWrapper>

                <AdditionalButtonsContainer>
                    {entries.length > 1 && (
                        <TertiaryButton
                            disabled={entries.length <= 1}
                            onClickHandler={removeEntry}
                            title={'Remove Analyst'}
                            style={{ marginRight: '1rem' }}
                        />
                    )}
                    <SecondaryButton
                        title="Add Analyst"
                        disabled={false}
                        onClickHandler={addEntry}
                    />
                </AdditionalButtonsContainer>

                <NextButton
                    disabled={disabled}
                    onClickHandler={() => {
                        setDisabled(true)
                        submitData()
                    }}
                />
            </IntakeCard>
        </>
    )
}

export default AnalystsQuestion
